import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Users, Sparkles, ArrowRight } from "lucide-react";
import {
    PROTEINS,
    SIDES,
    BEVERAGES,
    SERVICE_STYLES,
    EVENT_OPTIONS,
} from "@/lib/catering-data";

// Per-person price bands by service style, used for the *estimated* range.
const PRICING = {
    buffet: { min: 24, max: 34 },
    trays: { min: 19, max: 28 },
    plated: { min: 42, max: 65 },
    "private-chef": { min: 95, max: 140 },
};

const PROTEIN_RECS = {
    buffet: 2,
    trays: 2,
    plated: 1,
    "private-chef": 3,
};

const SIDE_RECS = {
    buffet: 3,
    trays: 3,
    plated: 2,
    "private-chef": 3,
};

const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function Section({ index, title, hint, children }) {
    return (
        <div className="border-t border-cream/[0.08] py-8 first:border-t-0 first:pt-0">
            <div className="flex items-baseline justify-between gap-6 mb-6">
                <div className="flex items-baseline gap-4">
                    <span className="font-serif italic text-gold/70 text-[14px] tracking-[0.15em]">
                        {String(index).padStart(2, "0")}
                    </span>
                    <h3 className="font-serif text-cream text-[1.4rem] md:text-[1.7rem] leading-tight tracking-tight">
                        {title}
                    </h3>
                </div>
                {hint && (
                    <span className="hidden md:inline text-cream/40 text-[11px] uppercase tracking-[0.22em]">
                        {hint}
                    </span>
                )}
            </div>
            {children}
        </div>
    );
}

function Chip({ active, children, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            aria-pressed={active}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-[12px] tracking-[0.06em] font-light transition-all duration-300 border ${
                active
                    ? "bg-burgundy text-cream border-burgundy"
                    : "bg-transparent text-cream/75 border-cream/15 hover:border-gold hover:text-gold"
            }`}
        >
            {active && <Check size={12} strokeWidth={2} />}
            {children}
        </button>
    );
}

export default function CateringBuilder() {
    const [guests, setGuests] = useState(50);
    const [eventType, setEventType] = useState("Wedding");
    const [proteins, setProteins] = useState(["jerk-chicken", "oxtail"]);
    const [sides, setSides] = useState(["rice-peas", "plantains", "collards"]);
    const [drinks, setDrinks] = useState(["tropical-rhythm"]);
    const [service, setService] = useState("buffet");

    const toggle = (list, setter) => (id) =>
        setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

    const summary = useMemo(() => {
        const band = PRICING[service];
        const min = guests * band.min;
        const max = guests * band.max;
        const proteinRec = PROTEIN_RECS[service];
        const sideRec = SIDE_RECS[service];

        return {
            min,
            max,
            band,
            proteinRec,
            sideRec,
            proteinsOk: proteins.length >= proteinRec - 0 && proteins.length <= proteinRec + 2,
            sidesOk: sides.length >= sideRec - 0 && sides.length <= sideRec + 2,
        };
    }, [guests, service, proteins, sides]);

    const labelFor = (list, id) => list.find((x) => x.id === id)?.label;

    const handleUseSelections = () => {
        const proteinLabels = proteins.map((id) => labelFor(PROTEINS, id)).filter(Boolean);
        const sideLabels = sides.map((id) => labelFor(SIDES, id)).filter(Boolean);
        const drinkLabels = drinks.map((id) => labelFor(BEVERAGES, id)).filter(Boolean);
        const serviceLabel = SERVICE_STYLES.find((s) => s.id === service)?.label;

        const message = [
            `Catering inquiry built via the package builder:`,
            ``,
            `· Guests: ${guests}`,
            `· Service style: ${serviceLabel}`,
            `· Proteins: ${proteinLabels.join(", ") || "—"}`,
            `· Sides: ${sideLabels.join(", ") || "—"}`,
            `· Beverages: ${drinkLabels.join(", ") || "—"}`,
            `· Estimated range: $${summary.min.toLocaleString()} – $${summary.max.toLocaleString()}`,
        ].join("\n");

        window.dispatchEvent(
            new CustomEvent("set-catering-builder", {
                detail: {
                    inquiry_type: "catering",
                    guest_count: guests,
                    event_type: eventType,
                    message,
                },
            })
        );

        scrollTo("catering-inquiry");
    };

    return (
        <section
            id="catering-builder"
            data-testid="catering-builder"
            aria-label="Build your catering package"
            className="relative empire-section bg-charcoal"
        >
            <div className="empire-container">
                {/* Header */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14 md:mb-20 items-end">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3">
                            <span className="h-px w-8 bg-gold" />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Plan with us
                            </span>
                        </div>
                        <h2 className="mt-5 font-serif text-cream text-[2.4rem] md:text-[3.4rem] lg:text-[4rem] leading-[1.04] tracking-[-0.01em]">
                            Build your catering package,<br />
                            <span className="italic text-gold">in under a minute.</span>
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:text-right">
                        <p className="empire-body max-w-md md:ml-auto">
                            Sketch out your event below. We&rsquo;ll use your
                            selections as a starting point — every menu is finalized
                            with our hospitality team.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
                    {/* Builder Panel */}
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-7 bg-charcoal-800 border border-cream/[0.08] p-7 md:p-12"
                    >
                        {/* 01 Guests */}
                        <Section index={1} title="Guest Count" hint="Slide to set">
                            <div className="flex items-center gap-6">
                                <Users size={18} className="text-gold/80 shrink-0" />
                                <input
                                    type="range"
                                    min={10}
                                    max={300}
                                    step={5}
                                    value={guests}
                                    onChange={(e) => setGuests(Number(e.target.value))}
                                    data-testid="builder-guests-slider"
                                    aria-label="Guest count"
                                    className="flex-1 accent-gold cursor-pointer"
                                />
                                <div className="font-serif text-gold text-[2.2rem] md:text-[2.8rem] leading-none tracking-tight w-24 md:w-28 text-right">
                                    {guests}
                                </div>
                            </div>
                            <p className="mt-3 text-cream/40 text-[12px] font-light tracking-wide">
                                Larger event? Just say so below — we cater up to 500+.
                            </p>
                        </Section>

                        {/* 02 Event Type */}
                        <Section index={2} title="Event Type">
                            <div className="flex flex-wrap gap-2">
                                {EVENT_OPTIONS.map((opt) => (
                                    <Chip
                                        key={opt}
                                        active={eventType === opt}
                                        onClick={() => setEventType(opt)}
                                        testid={`builder-event-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    >
                                        {opt}
                                    </Chip>
                                ))}
                            </div>
                        </Section>

                        {/* 03 Service Style */}
                        <Section index={3} title="Service Style">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {SERVICE_STYLES.map((s) => {
                                    const active = service === s.id;
                                    return (
                                        <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => setService(s.id)}
                                            data-testid={`builder-service-${s.id}`}
                                            aria-pressed={active}
                                            className={`group text-left p-5 border transition-colors duration-300 ${
                                                active
                                                    ? "border-gold bg-burgundy/15"
                                                    : "border-cream/[0.08] hover:border-gold/50 bg-transparent"
                                            }`}
                                        >
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="font-serif text-cream text-lg tracking-tight">
                                                    {s.label}
                                                </span>
                                                {active && (
                                                    <Check size={14} className="text-gold" />
                                                )}
                                            </div>
                                            <p className="mt-2 text-cream/55 text-[12.5px] font-light leading-relaxed">
                                                {s.note}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>
                        </Section>

                        {/* 04 Proteins */}
                        <Section
                            index={4}
                            title="Proteins"
                            hint={`We suggest ~${summary.proteinRec}`}
                        >
                            <div className="flex flex-wrap gap-2">
                                {PROTEINS.map((p) => (
                                    <Chip
                                        key={p.id}
                                        active={proteins.includes(p.id)}
                                        onClick={() => toggle(proteins, setProteins)(p.id)}
                                        testid={`builder-protein-${p.id}`}
                                    >
                                        {p.label}
                                        {p.vegan && (
                                            <span className="text-gold/70 text-[10px] ml-1">
                                                V
                                            </span>
                                        )}
                                    </Chip>
                                ))}
                            </div>
                        </Section>

                        {/* 05 Sides */}
                        <Section
                            index={5}
                            title="Sides"
                            hint={`We suggest ~${summary.sideRec}`}
                        >
                            <div className="flex flex-wrap gap-2">
                                {SIDES.map((s) => (
                                    <Chip
                                        key={s.id}
                                        active={sides.includes(s.id)}
                                        onClick={() => toggle(sides, setSides)(s.id)}
                                        testid={`builder-side-${s.id}`}
                                    >
                                        {s.label}
                                    </Chip>
                                ))}
                            </div>
                        </Section>

                        {/* 06 Beverages */}
                        <Section index={6} title="Beverages" hint="Optional">
                            <div className="flex flex-wrap gap-2">
                                {BEVERAGES.map((b) => (
                                    <Chip
                                        key={b.id}
                                        active={drinks.includes(b.id)}
                                        onClick={() => toggle(drinks, setDrinks)(b.id)}
                                        testid={`builder-bev-${b.id}`}
                                    >
                                        {b.label}
                                    </Chip>
                                ))}
                            </div>
                        </Section>
                    </motion.div>

                    {/* Summary Panel */}
                    <motion.aside
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                        aria-label="Package summary"
                        data-testid="builder-summary"
                        className="lg:col-span-5 lg:sticky lg:top-36 bg-burgundy-800/30 border border-gold/20 p-7 md:p-10"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <Sparkles size={16} className="text-gold" strokeWidth={1.5} />
                            <span className="empire-eyebrow !text-gold !text-[10px]">
                                Your Estimate
                            </span>
                        </div>

                        <div className="font-serif italic text-cream/85 text-[14px] tracking-wide">
                            {eventType} · {guests} guests
                        </div>

                        <div
                            data-testid="builder-price-range"
                            className="mt-4 font-serif text-gold text-[2.8rem] md:text-[3.4rem] leading-none tracking-tight"
                        >
                            ${summary.min.toLocaleString()}{" "}
                            <span className="text-cream/40 text-[1.6rem]">–</span>{" "}
                            ${summary.max.toLocaleString()}
                        </div>
                        <p className="mt-3 text-cream/50 text-[12px] font-light">
                            Estimated range. Pricing finalized with your dedicated event
                            host based on menu and service.
                        </p>

                        <ul className="mt-8 space-y-4 text-[13.5px]">
                            <li className="flex items-baseline justify-between gap-4">
                                <span className="text-cream/55 uppercase tracking-[0.18em] text-[10px]">
                                    Service
                                </span>
                                <span className="text-cream font-serif text-base">
                                    {SERVICE_STYLES.find((s) => s.id === service)?.label}
                                </span>
                            </li>
                            <li className="flex items-baseline justify-between gap-4">
                                <span className="text-cream/55 uppercase tracking-[0.18em] text-[10px]">
                                    Proteins
                                </span>
                                <span className="text-cream/85 text-right max-w-[60%] leading-snug">
                                    {proteins.length
                                        ? proteins
                                              .map((id) => labelFor(PROTEINS, id))
                                              .join(" · ")
                                        : "—"}
                                </span>
                            </li>
                            <li className="flex items-baseline justify-between gap-4">
                                <span className="text-cream/55 uppercase tracking-[0.18em] text-[10px]">
                                    Sides
                                </span>
                                <span className="text-cream/85 text-right max-w-[60%] leading-snug">
                                    {sides.length
                                        ? sides
                                              .map((id) => labelFor(SIDES, id))
                                              .join(" · ")
                                        : "—"}
                                </span>
                            </li>
                            <li className="flex items-baseline justify-between gap-4">
                                <span className="text-cream/55 uppercase tracking-[0.18em] text-[10px]">
                                    Beverages
                                </span>
                                <span className="text-cream/85 text-right max-w-[60%] leading-snug">
                                    {drinks.length
                                        ? drinks
                                              .map((id) => labelFor(BEVERAGES, id))
                                              .join(" · ")
                                        : "—"}
                                </span>
                            </li>
                        </ul>

                        <button
                            type="button"
                            onClick={handleUseSelections}
                            data-testid="builder-use-selections"
                            className="empire-btn-primary w-full mt-10 !py-4"
                        >
                            Use This For My Inquiry
                            <ArrowRight size={14} />
                        </button>
                        <p className="mt-4 text-cream/40 text-[11px] font-light text-center tracking-wide">
                            We respond within one business day.
                        </p>
                    </motion.aside>
                </div>
            </div>
        </section>
    );
}
